import { DatePipe, DecimalPipe, Location } from "@angular/common";
import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { NgForm } from "@angular/forms";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { DateTime } from "luxon";
import { Observable, of, throwError } from "rxjs";
import { catchError, flatMap, map } from "rxjs/operators";
import { conformToMask } from "text-mask-core";

import { environment } from "../../../environments/environment";
import { TicketCutoffTypeEnum } from "../../enum/cutoff-type.enum";
import { MenuConstants } from "../../infrastructure/MenuConstants";
import { CurrencyTerm } from "../../model/domain/CurrencyTerm";
import {
  EntryType,
  Portfolio,
  TicketStatus,
  TicketType,
} from "../../model/domain/Entity";
import { NamedType } from "../../model/domain/Tipo";
import { AuthService } from "../../services/auth.service";
import { CutoffService } from "../../services/cutoff.service";
import { DialogService } from "../../services/dialog.service";
import { FileService } from "../../services/file.service";
import { LoaderService } from "../../services/loader.service";
import { SnackbarService } from "../../services/snackbar.service";
import { TermoMoedaService } from "../../services/termo-moeda.service";
import { TicketService } from "../../services/ticket.service";
import { ValidatorService } from "../../services/validator.service";
import { AnnotationComponent } from "../annotation/annotation.component";
import { Roles } from "../auth/auth.role";
import { CounterpartComponent } from "../counterpart/counterpart.component";
import { TicketCutoffPermission } from "../cutoff/ticket-cutoff-type.model";
import { DadosFundoComponent } from "../dados-fundo/dados-fundo.component";

@Component({
  selector: "app-termo-moeda",
  templateUrl: "./termo-moeda.component.html",
  styleUrls: ["./termo-moeda.component.css"],
})
export class TermoMoedaComponent implements OnInit, OnDestroy {
  decimalPipe = new DecimalPipe("pt");

  @ViewChild("thisForm", { static: true }) frm: NgForm;
  @ViewChild("portfolio", { static: true }) fundoComponent: DadosFundoComponent;
  @ViewChild("counterpart", { static: true })
  counterpartComponent: CounterpartComponent;
  @ViewChild("annotation", { static: true })
  annotationComponent: AnnotationComponent;

  TicketTypeEnum = TicketType;
  EntryTypeEnum = EntryType;

  dataChanged: EventEmitter<string>;
  isNotProduction = environment.name != "production";

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private service: TicketService,
    public validator: ValidatorService,
    private datePipe: DatePipe,
    private dialogService: DialogService,
    private snackBarService: SnackbarService,
    private termoService: TermoMoedaService,
    private authService: AuthService,
    private location: Location,
    private loaderService: LoaderService,
    private fileService: FileService,
    private cutoffService: CutoffService
  ) {
    this.navigationSubscription = this.router.events.subscribe((e: any) => {
      if (e instanceof NavigationEnd) {
        this.resetForm();
      }
    });
  }

  validators = {
    expirationDate: (value) => this.model.expirationDate != null && value != "",
    entryTypeId: (value) => this.EntryTypeValidators.includes(value),
    futureFee: (value) =>
      this.coalesce(value).length > 0 &&
      this.validator.getCurrency(value) <= 100,
    interestFee: (value) =>
      this.isEarlySettlement() ? this.coalesce(value).length > 0 : true,
    baseValue: (value) =>
      this.coalesce(value).length === 0 ||
      conformToMask(value, this.validator.currencyMask, {}),
    financialValue: (value) =>
      this.coalesce(value).length === 0 ||
      conformToMask(value, this.validator.currencyMask, {}),
  };

  navigationSubscription;

  isEditing = false;
  canValidate = false;
  model: CurrencyTerm = null;

  Moedas: NamedType<number>[] = null;
  TiposOperacao: NamedType<number>[] = null;

  listaCotacaovencimento: NamedType<number>[];

  TiposLancamento: NamedType<number>[] = null;

  EntryTypeValidators = [];

  ngOnInit() {
    this.dataChanged = new EventEmitter<string>();

    this.TiposLancamento = this.termoService.ListarTiposLancamento();

    this.EntryTypeValidators = this.TiposLancamento.map((t) => t.id);

    this.getTicket();
    this.modelInit();
  }

  ngOnDestroy() {
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
  }

  resetForm() {
    this.model = new CurrencyTerm();

    // TODO: verificar o motivo do model estar desincronizando com a tela
    // this.frm.reset();
    // this.frm.resetForm();

    this.fundoComponent.resetForm();
    this.fundoComponent.portfolio = new Portfolio();
    this.model.id = 0;
    this.annotationComponent.annotation = null;

    this.model.portfolio = this.fundoComponent.portfolio;

    this.isEditing = false;
    this.canValidate = false;

    this.model.operationDate = new Date();
    this.model.cetipSettlement = true;

    this.modelInit();

    this.dataChanged = new EventEmitter<string>();
    this.setEnable(true);
  }

  modelInit() {}

  setEnable(enabled: boolean = false) {
    setTimeout(() => {
      if (enabled) {
        this.fundoComponent.setEnable(true);
        Object.values(this.frm.controls).forEach((c) => {
          c.enable();
        });
      } else if (!this.canEdit()) {
        this.fundoComponent.setEnable(false);
        this.annotationComponent.annotationIsDisabled = true;
        Object.values(this.frm.controls).forEach((c) => {
          c.disable();
        });
      }
    }, 10);
  }

  getTicket(idBoleta: number = null) {
    this.model = new CurrencyTerm();
    this.TiposOperacao = this.termoService.ListarTipoOperacaoTermoMoeda();
    this.listaCotacaovencimento = this.termoService.ListaCotacaoVencimento();
    this.Moedas = this.termoService.ListarMoedas();

    const id =
      idBoleta === null ? +this.route.snapshot.paramMap.get("id") : idBoleta;
    this.model.id = id;

    if (id) {
      this.service.CurrencyTerm.getTicket(id).subscribe(
        (q) => {
          Object.assign(this.model, q);
          if (this.model.expirationDate) {
            this.model.expirationDate = this.datePipe.transform(
              this.model.expirationDate,
              "yyyy-MM-dd"
            ) as any;
          }

          if (this.model.operationDate) {
            this.model.operationDate = new Date(this.model.operationDate);
          }

          this.model.baseValue = this.decimalPipe.transform(
            this.model.baseValue,
            "1.0-2",
            "pt-BR"
          ) as any;
          this.model.futureFee = this.decimalPipe.transform(
            this.model.futureFee,
            "1.0-8",
            "pt-BR"
          ) as any;

          this.model.financialValue =
            this.model.financialValue === 0
              ? null
              : (this.decimalPipe.transform(
                  this.model.financialValue,
                  "1.0-4",
                  "pt-BR"
                ) as any);

          this.model.interestFee =
            this.model.interestFee === 0
              ? null
              : (this.decimalPipe.transform(
                  this.model.interestFee,
                  "1.0-4",
                  "pt-BR"
                ) as any);

          this.fundoComponent.fillComponentFieldsLoad(this.model);

          this.counterpartComponent.fillComponentFieldsLoad(this.model);

          this.isEditing = true;
          this.canValidate = true;

          this.dataChanged.emit("");

          this.setEnable(false);
        },
        () => this.router.navigate([MenuConstants.Tickets.CurrencyTerm])
      );
    } else {
      if (!this.model.statusId) {
        this.model.operationDate = new Date();
      }
    }
  }

  submit() {
    this.loaderService.addLoader();

    const editing = this.isEditing;

    this.register(this.frm)
      .pipe(
        catchError((err) => {
          this.loaderService.removeLoader();

          this.snackBarService.open(err, "OK", {
            verticalPosition: "top",
            horizontalPosition: "right",
          });
          return throwError(err);
        })
      )
      .subscribe(
        (r) => {
          this.loaderService.removeLoader();

          const msg = editing ? "Boleta alterada." : "Boleta cadastrada.";

          this.snackBarService.open(msg, "OK", {
            verticalPosition: "top",
            horizontalPosition: "right",
          });
        },
        () => {}
      );
  }

  register(f: NgForm, duplicate: boolean = false): Observable<CurrencyTerm> {
    const editing = this.isEditing;

    if (!this.canEdit() && !duplicate) {
      return;
    }

    var hasAttachment = false;

    if (
      this.model.id != 0 ||
      (this.model.id == 0 && this.model.tempTicketId != null)
    ) {
      this.fileService
        .getFilesPaginatedAttachment(
          this.model.tempTicketId,
          this.model.id,
          1,
          10
        )
        .subscribe((files) => {
          hasAttachment = files != null && files.rowCount > 0;

          if (!hasAttachment) {
            return throwError("Documento inválido");
          }
        });
    }

    Object.values(f.controls).forEach((c) => {
      c.markAsTouched();
      c.updateValueAndValidity();
    });

    this.canValidate = true;

    if (f.invalid) {
      return throwError("Verifique os campos destacados.");
    }

    const errors = this.getErrors();

    this.fundoComponent.validatePortfolioFields();
    const fundErrors = this.fundoComponent.getErrors();
    const counterpartErrors = this.counterpartComponent.getErrors();

    if (f.invalid || errors.length > 0) {
      return throwError("Verifique os campos destacados.");
    }

    if (fundErrors.length > 0) {
      return throwError("Verifique os campos nos dados do Fundo Investidor.");
    }

    if (counterpartErrors.length > 0) {
      return throwError("Verifique os campos nos dados da Contraparte.");
    }

    let data = Object.assign({}, this.model);
    data.id = null;

    data.counterpart.document = this.validator.sanitize(
      this.model.counterpart.document
    );

    data.baseValue = this.validator.getCurrency(this.model.baseValue);
    data.futureFee = this.validator.getCurrency(this.model.futureFee);
    data.interestFee = this.validator.getCurrency(this.model.interestFee);
    data.financialValue = this.validator.getCurrency(this.model.financialValue);
    data.entryTypeId = this.validator.getCurrency(this.model.entryTypeId);

    data.expirationDate = new Date(
      this.model.expirationDate
        .toString()
        .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
    );

    data = this.fundoComponent.fillPortfolioData(
      data,
      this.fundoComponent.portfolio
    );

    data = this.counterpartComponent.fillCounterpartData(
      data,
      this.counterpartComponent.counterpartModel
    );

    data.annotations = this.annotationComponent.annotation;

    if (duplicate) {
      data.workflowStartDate = null;
      data.workflowEndDate = null;
    }

    const method =
      editing && !duplicate
        ? this.service.CurrencyTerm.updateTicket(this.model.id, data)
        : this.service.CurrencyTerm.registerTicket(data);

    return method
      .pipe(
        catchError((err) => {
          return throwError("Erro ao cadastrar a boleta.");
        })
      )
      .pipe(
        map((term: CurrencyTerm) => {
          if (!editing) {
            this.model.id = term.id;
            this.model.operationDate = term.operationDate;
            this.model.statusId = term.statusId;
            this.model.accountManagerName = term.accountManagerName;
            this.model.accountManagerDocument = term.accountManagerDocument;
            this.model.ticketCutoffTypeId = term.ticketCutoffTypeId;

            this.isEditing = true;

            const url = this.router.createUrlTree([this.model.id], {
              relativeTo: this.route,
            });
            this.location.go(url.toString());
          }

          return this.model;
        })
      );
  }

  confirmForward(): Promise<boolean> {
    if (!this.canEdit()) {
      return Promise.resolve(false);
    }

    var textConfirm = "A boleta não poderá mais ser editada, deseja continuar?";

    if (this.isNotProduction) {
      textConfirm +=
        "<br />ATENÇÃO: ESTE É UM AMBIENTE DE TESTE. NÃO EXECUTE OPERAÇÕES REAIS.";
    }

    return this.dialogService
      .confirm("Encaminhar Boleta", textConfirm)
      .toPromise()
      .then((result) => {
        if (!result) {
          return false;
        }

        const operationDate = DateTime.fromJSDate(
          new Date(this.model.operationDate.toString())
        );

        if (operationDate.day < DateTime.local().day) {
          return this.dialogService
            .confirm(
              "Boleta Retroativa",
              "A data de operação da boleta será alterada para a data de hoje.",
              "OK",
              "Cancelar"
            )
            .toPromise();
        }

        return true;
      });
  }

  async forward(): Promise<void> {
    const cutoffPermission = await this.checkPermission();

    if (!cutoffPermission.isAllowed) {
      this.snackBarService.open(
        this.cutoffService.warningMessage +
          this.cutoffService.dateTimeOffsetToTimeString(
            cutoffPermission.timeLimit
          ),
        "OK"
      );
      return;
    }

    this.confirmForward().then((ok) => {
      if (!ok) {
        return;
      }

      this.register(this.frm)
        .pipe(
          catchError((err) => {
            this.snackBarService.open(err, "OK", {
              verticalPosition: "top",
              horizontalPosition: "right",
            });
            return throwError(err);
          })
        )
        .subscribe(
          (term: CurrencyTerm) => {
            this.service.CurrencyTerm.forwardTicket(term.id).subscribe(
              (res) => {
                this.model.statusDescription = res.statusDescription;

                this.setEnable(false);
                this.snackBarService.open("Boleta encaminhada.", "OK", {
                  verticalPosition: "top",
                  horizontalPosition: "right",
                });

                this.getTicket(term.id);
              }
            );
          },
          () => {}
        );
    });
  }

  cancel() {
    return this.dialogService
      .confirm("Cancelar Boleta", "Deseja cancelar esta boleta?")
      .pipe(
        flatMap((ok) => {
          if (ok) {
            return this.service.CurrencyTerm.cancelDraftTicket(this.model.id);
          }

          return of(null);
        })
      )
      .subscribe((res) => {
        if (res) {
          this.model.statusDescription = res.statusDescription;

          Object.values(this.frm.controls).forEach((c) => {
            c.disable();
          });

          this.snackBarService.open("Boleta cancelada.", "OK", {
            verticalPosition: "top",
            horizontalPosition: "right",
          });

          this.getTicket(this.model.id);
        }
      });
  }

  approve() {
    this.dialogService
      .confirm("Aprovar Boleta", "Deseja aprovar esta boleta?")
      .subscribe((result) => {
        if (result) {
          this.service.approve(this.model.id, "").subscribe(() => {
            this.snackBarService.open("Boleta atualizada com sucesso.", "OK", {
              verticalPosition: "top",
              horizontalPosition: "right",
            });

            this.getTicket(this.model.id);
          });
        }
      });
  }

  reject() {
    this.dialogService
      .reject("Reprovar Boleta", "Deseja reprovar esta boleta?")
      .subscribe((result) => {
        if (result) {
          this.service
            .reject(this.model.id, result.justification)
            .subscribe(() => {
              this.snackBarService.open(
                "Boleta atualizada com sucesso.",
                "OK",
                {
                  verticalPosition: "top",
                  horizontalPosition: "right",
                }
              );
              this.getTicket(this.model.id);
            });
        }
      });
  }

  duplicate() {
    this.register(this.frm, true).subscribe((newTicket) => {
      this.snackBarService
        .open("Boleta duplicada.", "Visualizar no Monitor", {
          verticalPosition: "top",
          horizontalPosition: "right",
          duration: 6000,
        })
        .onAction()
        .subscribe((a) => {
          this.router.navigate([
            MenuConstants.Todos,
            { ticketId: TicketType.CurrencyTerm },
          ]);
        });
    });
  }

  coalesce(value: any) {
    return value || "";
  }

  hasError(prop: string) {
    if (!this.canValidate) {
      return false;
    }

    return !this.validators[prop](this.model[prop]);
  }

  getErrors(): string[] {
    const validationErrors = [];

    Object.keys(this.validators).forEach((k) => {
      if (!this.validators[k](this.model[k])) {
        validationErrors.push(k);
      }
    });

    return validationErrors;
  }

  canEdit() {
    return (
      !this.model.statusId &&
      !this.model.statusDescription &&
      this.authService.hasRole(Roles.CreateTicketFundQuota)
    );
  }

  isEarlySettlement(): boolean {
    return this.model.entryTypeId === EntryType.EarlySettlement;
  }

  async checkPermission(): Promise<TicketCutoffPermission> {
    let cutoffTypeId = this.model.ticketCutoffTypeId;
    let accountManagerId = this.model.accountManagerId;
    let fundId = this.model.portfolio.fundId;

    if (accountManagerId === null) {
      cutoffTypeId = TicketCutoffTypeEnum.CurrencyTerm;
      accountManagerId = this.authService.authToken.accountManagerId;
      fundId = this.fundoComponent.portfolio.fundId;
    }
    return this.cutoffService.isAvailable(
      cutoffTypeId,
      this.model.id,
      accountManagerId,
      fundId
    );
  }

  canDuplicate() {
    const _operationDate = this.datePipe.transform(
      this.model.operationDate,
      "yyyy-MM-dd"
    );
    const _duplicateTicketDateLimit = this.datePipe.transform(
      new Date(environment.duplicateTicketDateLimit),
      "yyyy-MM-dd"
    );

    return (
      this.model.id &&
      this.authService.hasRole(Roles.CreateTicketFundQuota) &&
      !(_operationDate <= _duplicateTicketDateLimit)
    );
  }

  canChangeStatus() {
    return (
      this.model.statusId &&
      this.authService.hasRoles(this.model.statusRequiredRoles)
    );
  }

  canCancel() {
    return (
      this.model.id &&
      (!this.model.statusId || this.model.statusId === TicketStatus.Draft) &&
      this.authService.hasRole(Roles.CreateTicketFundQuota)
    );
  }

  setTempId(tempId) {
    this.model.tempTicketId = tempId;
  }
}
